<template>
    <v-form>
        <div class="grid">
            <FormInputNumber fieldName="premium_year"
                             label="Year"
                             size="2"
                             :useGrouping="false"
                             :min="2011"
                             :isFocus="true"
                             v-model:field="premium_year" />

            <FormDropdown fieldName="premium_quarter"
                          label="Quarter"
                          size="5"
                          :isFormDisabled="assessmentPeriod == 1"
                          :filtering="false"
                          :setFirst="false"
                          :options="select?.quarter_periods"
                          v-model:field="premium_quarter" />

            <FormDropdown fieldName="premium_month"
                          label="Month"
                          size="5"
                          :isFormDisabled="assessmentPeriod == 3"
                          :filtering="false"
                          :options="select?.month_periods"
                          v-model:field="premium_month" />
        </div>
        <div class="grid"
             style="margin:20px 0 10px 0;font-size:18px;color:green;font-weight:bold;">
            {{ __('Copy from...') }}
        </div>
        <div class="grid"
             style="background-color:#ccc">
            <FormInputNumber fieldName="source_premium_year"
                             label="Year"
                             size="2"
                             :useGrouping="false"
                             :min="2011"
                             v-model:field="source_premium_year" />
            <FormDropdown fieldName="source_premium_quarter"
                          label="Quarter"
                          size="3"
                          :isFormDisabled="assessmentPeriod == 1"
                          :filtering="false"
                          :setFirst="false"
                          :options="select?.quarter_periods"
                          v-model:field="source_premium_quarter" />

            <FormDropdown fieldName="source_premium_month"
                          label="Month"
                          size="3"
                          :isFormDisabled="assessmentPeriod == 3"
                          :filtering="false"
                          :options="select?.month_periods"
                          v-model:field="source_premium_month" />

            <FormDropdown fieldName="source_worker_id"
                          label="Worker"
                          size="4"
                          :isFormDisabled="false"
                          :isFiltering="true"
                          :filter="(event) => onFilter(event, 'myworkers')"
                          :options="select?.myworkers"
                          v-model:field="source_worker_id" />
        </div>

        <div class="grid"
             style="margin-top:10px">
            <FormInputToggleButton fieldName="is_copy_assessments"
                                   label="Wheter to copy assessments too?"
                                   size="6"
                                   :isFormDisabled="isFormDisabled"
                                   v-model:field="is_copy_assessments" />
        </div>

        <div class="grid"
             style="margin-top:20px">
            <Button severity="success"
                    style="margin:0 8px"
                    @click="createNewSheet">
                <i class="fa-solid fa-plus"
                   style="margin-right:5px;font-size:18px"></i>
                {{ __('Create') }}
            </Button>
            <Button severity="secondary"
                    style="margin:0 8px"
                    @click="cancel">
                <i class="fa-solid fa-ban"
                   style="margin-right:5px;font-size:18px"></i>
                {{ __('Cancel') }}
            </Button>
        </div>
    </v-form>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useForm } from '@inertiajs/vue3'
import Button from 'primevue/button';
import FormDropdown from '@crud/FormDropdown.vue'
import FormInputNumber from '@crud/FormInputNumber.vue'
import FormInputToggleButton from '@crud/FormInputToggleButton.vue'
import { onFilterService, initForm, prepareSelect } from '@pages/Helper/crud_utils.js'
import { __, screenMessage, getStoreValue, currentDate, currentYear, currentQuarter, currentMonth, getFromRoute, getTimeouts } from '@pages/Helper/utils.js'

// -----------------------------------------------------
const props = defineProps({
    formProps: Object,
    workerId: Number,
    getLazyData: Function,
})

const isNewSheetConditionModalOpen = defineModel('isNewSheetConditionModalOpen')
const initialConditions = defineModel('initialConditions')

const isLoading = ref(true);
const premium_year = ref(null)
const premium_quarter = ref(null)
const premium_month = ref(null)
const version_date = ref(props.formProps.versionDate)
const sheet_number = ref(null)
const assessmentPeriod = ref(null)

const source_premium_year = ref(null)
const source_premium_quarter = ref(null)
const source_premium_month = ref(null)
const source_worker_id = ref(null)
const is_copy_assessments = ref(false)

let select = ref(null)

// -----------------------------------------------------

onMounted(async () => {
    isLoading.value = true;
    select.value = await prepareSelect(["quarter_periods", "month_periods", "myworkers"]);
    const currentPeriod = getStoreValue('premiumPeriod')
    isLoading.value = false;
    await checkPremiumGroup()

    premium_year.value = parseInt(currentPeriod?.premium_year ?? currentYear())
    source_premium_year.value = parseInt(currentPeriod?.premium_year ?? currentYear())
    premium_quarter.value = parseInt(currentPeriod?.premium_quarter ?? currentQuarter(true))
    premium_month.value = parseInt(currentPeriod?.premium_month ?? currentMonth(true))
})

// -----------------------------------------------------

const checkPremiumGroup = async () => {
    const period = await getFromRoute('premium-period', { premiumId: props.formProps.premiumId })
    assessmentPeriod.value = parseInt(period?.assessment_period)
}

// -----------------------------------------------------

const cancel = () => {
    isNewSheetConditionModalOpen.value = false
}
// -----------------------------------------------------

const createNewSheet = async () => {
    const canCreate = await checkIfCanCreateSheet()
    if (!canCreate) {
        return false
    }

    initialConditions.value = {
        premium_year: premium_year.value,
        premium_quarter: premium_quarter.value,
        premium_month: premium_month.value,
        version_date: version_date.value,
        sheet_number: sheet_number.value,
    }
    isNewSheetConditionModalOpen.value = false
    if (source_premium_year.value && source_worker_id.value && (source_premium_quarter.value || source_premium_month.value)) {
        copySheetAndSaveAsReady()
    } else {
        props.formProps.openModalAdd()
    }
}

// -----------------------------------------------------

const checkIfCanCreateSheet = async () => {
    const checkNewSheetConditions = await getFromRoute('sheet-can-create', { workerId: props.formProps.workerId, premiumYear: premium_year.value, premiumQuarter: premium_quarter.value, premiumMonth: premium_month.value }, 'post')

    if (!checkNewSheetConditions || checkNewSheetConditions.sheetExists || !checkNewSheetConditions.sheetInRange || !checkNewSheetConditions.premiumGroupDefined) {
        const timeouts = getTimeouts()
        const message = {
            message: checkNewSheetConditions?.msg || __("Cannot create sheet for given period"),
            title: __("Cannot create sheet for given period"),
            type: "error",
            position: "center",
            timeout: timeouts.error,
        };

        screenMessage(message);
        return false
    }
    sheet_number.value = checkNewSheetConditions.sheetNumber
    return true
}

// -----------------------------------------------------

const copySheetAndSaveAsReady = async () => {
    const response = await getFromRoute('sheet-copy', {
        dest_worker_id: props.formProps.workerId,
        dest_premium_year: premium_year.value,
        dest_premium_period: assessmentPeriod.value == 1 ? premium_month.value : premium_quarter.value,

        source_worker_id: source_worker_id.value,
        source_premium_year: source_premium_year.value,
        source_premium_period: assessmentPeriod.value == 1 ? source_premium_month.value : source_premium_quarter.value,

        during_create_new_sheet: true,
        is_copy_assessments: is_copy_assessments.value,
    }, 'post')

    const timeouts = getTimeouts()
    if (response?.status == 'success') {
        props.getLazyData()
        screenMessage({ message: response?.message, title: "Information", type: "info", position: "center", timeout: timeouts.info });
    } else {
        screenMessage({ message: response?.message, title: "Information", type: "error", position: "center", timeout: timeouts.error });
    }
}

// -----------------------------------------------------

const onFilter = async (event, option) => {
    await onFilterService(event, option, select, isLoading)
}

// -----------------------------------------------------
</script>

<style lang="scss" scoped></style>
